import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { useTranslation } from "../i18n";
import { useAuth } from "../auth/AuthProvider";
import { AuthShell } from "../components/AuthShell";
import { Alert, Spinner, buttonStyles } from "../components/ui";
import { ApiError } from "../lib/api";

/**
 * Where GitHub or GitLab lands the person, via the API.
 *
 * The URL carries a one-time code and nothing else worth having: the page
 * exchanges it over XHR for the same answer the password form gets -- a
 * session, or a second-factor challenge, which is handed to the sign-in
 * page to run the step it already knows. Or it carries a refusal, and the
 * page says it in the person's language with the way back.
 */
export function OAuthSigninPage() {
  const { t } = useTranslation("auth");
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { exchangeOAuth } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  const code = params.get("code");
  const refusal = params.get("error");
  const message = params.get("message");
  const raw = params.get("return_to") ?? "/apps";
  const returnTo = raw.startsWith("/") && !raw.startsWith("//") ? raw : "/apps";

  useEffect(() => {
    if (!code || started.current) return;
    // Once. A strict-mode double mount must not spend the code twice.
    started.current = true;
    void (async () => {
      try {
        const outcome = await exchangeOAuth(code);
        if (outcome.kind === "challenge") {
          void navigate("/login", { replace: true, state: { from: returnTo, oauthChallenge: outcome } });
          return;
        }
        // A new account lands where a confirmed password signup lands; the empty
        // apps page is the welcome.
        void navigate(outcome.created ? "/apps" : returnTo, { replace: true });
      } catch (cause) {
        setError(cause instanceof ApiError ? cause.message : t("social.failed"));
      }
    })();
  }, [code, exchangeOAuth, navigate, returnTo, t]);

  const reason =
    error ??
    (refusal === "refused" && message
      ? message
      : refusal === "cancelled"
        ? t("social.cancelled")
        : refusal === "expired"
          ? t("social.expired")
          : refusal
            ? t("social.failed")
            : null);

  /*
   * The next step, worded for the refusal. A cancelled sign-in goes back to
   * the form; a provider account too new, or with no verified address, is
   * told the other door -- register with an email -- rather than left with a
   * dead end and a sentence about what went wrong.
   */
  const registerInstead = refusal === "refused" && Boolean(message);

  return (
    <AuthShell title={reason ? t("social.failedTitle") : t("social.finishing")} subtitle={reason ? undefined : t("social.oneMoment")}>
      {reason ? (
        <div className="space-y-4">
          <Alert tone={refusal === "cancelled" ? "info" : "error"}>{reason}</Alert>
          <Link to="/login" className={buttonStyles("primary", "xl", "w-full")}>
            {t("social.backToSignIn")}
          </Link>
          {registerInstead && (
            <Link to="/register" className={buttonStyles("secondary", "xl", "w-full")}>
              {t("social.registerWithEmail")}
            </Link>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-3 text-sm text-fg-subtle" role="status" aria-live="polite">
          <Spinner className="size-4" />
          {t("social.checking")}
        </div>
      )}
    </AuthShell>
  );
}
